import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  SpaceBetween,
  Box,
  Badge,
  Modal,
  Form,
  FormField,
  Input,
  Select,
  Textarea,
  TokenGroup,
  Alert
} from '@cloudscape-design/components';
import { titleAPI } from '../services/api';

const VERSION_TYPES = [
  { label: 'VO (Version Originale)', value: 'VO' },
  { label: 'VF (Version Française)', value: 'VF' },
  { label: "Director's Cut", value: "Director's Cut" },
  { label: 'Extended Cut', value: 'Extended Cut' },
  { label: 'Theatrical Cut', value: 'Theatrical Cut' },
  { label: 'Version Kids', value: 'Version Kids' },
  { label: 'Version Censored', value: 'Version Censored' },
  { label: 'International Cut', value: 'International Cut' },
  { label: 'Final Cut', value: 'Final Cut' }
];

const KNOWN_MAM_PROVIDERS = [
  { label: 'MAM Light (MSC)', value: 'mam_light' },
  { label: 'Dalet Galaxy', value: 'dalet' },
  { label: 'Avid MediaCentral', value: 'avid' },
  { label: 'Harmonic VOS', value: 'harmonic' },
  { label: 'Adobe Premiere', value: 'adobe' },
  { label: 'Nomad (Final Cut)', value: 'nomad' },
  { label: 'AWS Elemental', value: 'aws_elemental' },
  { label: 'Custom...', value: '__custom__' },
];

const VersionsTab = ({ titleId }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    version_type: 'VO',
    version_name: '',
    package_ids: [],
    external_ids: {},
    audio_languages: [],
    subtitle_languages: []
  });
  const [error, setError] = useState('');
  const [newExternalKey, setNewExternalKey] = useState('');
  const [newExternalValue, setNewExternalValue] = useState('');
  const [newExternalCustomKey, setNewExternalCustomKey] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(null);

  useEffect(() => {
    loadVersions();
  }, [titleId]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const response = await titleAPI.listVersions(titleId);
      setVersions(response.data.versions || []);
    } catch (error) {
      setError('Failed to load versions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVersion = () => {
    setFormData({
      version_type: 'VO',
      version_name: '',
      package_ids: [],
      external_ids: { mam_light: '' },
      audio_languages: [],
      subtitle_languages: []
    });
    setNewExternalKey('');
    setNewExternalValue('');
    setSelectedVersion(null);
    setShowModal(true);
  };

  const handleEditVersion = (version) => {
    setFormData({
      version_type: version.version_type,
      version_name: version.version_name || '',
      package_ids: version.package_ids || [],
      external_ids: version.external_ids || {},
      audio_languages: version.audio_languages || [],
      subtitle_languages: version.subtitle_languages || []
    });
    setSelectedVersion(version);
    setShowModal(true);
  };

  const handleSaveVersion = async () => {
    try {
      const versionData = {
        ...formData,
        title_id: titleId
      };

      if (selectedVersion) {
        await titleAPI.updateVersion(titleId, selectedVersion.version_id, versionData);
      } else {
        await titleAPI.createVersion(titleId, versionData);
      }

      setShowModal(false);
      loadVersions();
    } catch (error) {
      setError('Failed to save version');
    }
  };

  const handleDeleteVersion = async (versionId) => {
    if (window.confirm('Are you sure you want to delete this version?')) {
      try {
        await titleAPI.deleteVersion(titleId, versionId);
        loadVersions();
      } catch (error) {
        setError('Failed to delete version');
      }
    }
  };

  const addPackageId = (packageId) => {
    if (packageId && !formData.package_ids.includes(packageId)) {
      setFormData({
        ...formData,
        package_ids: [...formData.package_ids, packageId]
      });
    }
  };

  const removePackageId = (packageId) => {
    setFormData({
      ...formData,
      package_ids: formData.package_ids.filter(id => id !== packageId)
    });
  };

  const addLanguage = (language, type) => {
    if (language && !formData[type].includes(language)) {
      setFormData({
        ...formData,
        [type]: [...formData[type], language]
      });
    }
  };

  const removeLanguage = (language, type) => {
    setFormData({
      ...formData,
      [type]: formData[type].filter(lang => lang !== language)
    });
  };

  const columnDefinitions = [
    {
      id: 'version_id',
      header: 'Version ID',
      cell: item => item.version_id
    },
    {
      id: 'version_type',
      header: 'Type',
      cell: item => <Badge>{item.version_type}</Badge>
    },
    {
      id: 'version_name',
      header: 'Name',
      cell: item => item.version_name || '-'
    },
    {
      id: 'package_ids',
      header: 'Package IDs',
      cell: item => (
        <SpaceBetween direction="horizontal" size="xs">
          {(item.package_ids || []).slice(0, 3).map(id => (
            <Badge key={id} color="blue">{id}</Badge>
          ))}
          {item.package_ids?.length > 3 && (
            <Badge>+{item.package_ids.length - 3} more</Badge>
          )}
        </SpaceBetween>
      )
    },
    {
      id: 'external_ids',
      header: 'External IDs',
      cell: item => {
        const ids = item.external_ids || {};
        const entries = Object.entries(ids).filter(([, v]) => v);
        if (entries.length === 0) return '-';
        return (
          <SpaceBetween direction="horizontal" size="xs">
            {entries.map(([system, id]) => (
              <Badge key={system} color="green">{system}: {id}</Badge>
            ))}
          </SpaceBetween>
        );
      }
    },
    {
      id: 'audio_languages',
      header: 'Audio',
      cell: item => (item.audio_languages || []).join(', ') || '-'
    },
    {
      id: 'subtitle_languages',
      header: 'Subtitles',
      cell: item => (item.subtitle_languages || []).join(', ') || '-'
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: item => (
        <SpaceBetween direction="horizontal" size="xs">
          <Button size="small" onClick={() => handleEditVersion(item)}>
            Edit
          </Button>
          <Button 
            size="small" 
            variant="link"
            onClick={() => handleDeleteVersion(item.version_id)}
          >
            Delete
          </Button>
        </SpaceBetween>
      )
    }
  ];

  return (
    <>
      <SpaceBetween size="l">
        {error && <Alert type="error">{error}</Alert>}
        
        <Table
          columnDefinitions={columnDefinitions}
          items={versions}
          loading={loading}
          loadingText="Loading versions..."
          empty={
            <Box textAlign="center" color="inherit">
              <b>No versions</b>
              <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                No editorial versions found for this title.
              </Box>
              <Button onClick={handleCreateVersion}>Create version</Button>
            </Box>
          }
          header={
            <SpaceBetween direction="horizontal" size="xs">
              <Box variant="h3">Editorial Versions</Box>
              <Button variant="primary" onClick={handleCreateVersion}>
                Create Version
              </Button>
            </SpaceBetween>
          }
        />
      </SpaceBetween>

      <Modal
        visible={showModal}
        onDismiss={() => setShowModal(false)}
        header={selectedVersion ? 'Edit Version' : 'Create Version'}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveVersion}>
                {selectedVersion ? 'Update' : 'Create'}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <SpaceBetween size="l">
            <FormField label="Version Type">
              <Select
                selectedOption={VERSION_TYPES.find(t => t.value === formData.version_type)}
                onChange={({ detail }) => setFormData({
                  ...formData,
                  version_type: detail.selectedOption.value
                })}
                options={VERSION_TYPES}
              />
            </FormField>

            <FormField label="Version Name" description="Optional custom name for this version">
              <Input
                value={formData.version_name}
                onChange={({ detail }) => setFormData({
                  ...formData,
                  version_name: detail.value
                })}
                placeholder="e.g., Director's Cut - Special Edition"
              />
            </FormField>

            <FormField label="Package IDs" description="MaM package identifiers">
              <SpaceBetween size="s">
                <TokenGroup
                  items={formData.package_ids.map(id => ({ label: id, value: id }))}
                  onDismiss={({ detail }) => removePackageId(detail.itemIndex)}
                />
                <Input
                  placeholder="Enter package ID and press Enter"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addPackageId(e.target.value);
                      e.target.value = '';
                    }
                  }}
                />
              </SpaceBetween>
            </FormField>

            <FormField label="External IDs" description="Link to MAM systems and other external platforms">
              <SpaceBetween size="s">
                {Object.entries(formData.external_ids || {}).map(([key, value]) => (
                  <SpaceBetween key={key} direction="horizontal" size="xs">
                    <Input
                      value={key}
                      placeholder="System name"
                      readOnly
                      style={{ width: '150px' }}
                    />
                    <Input
                      value={value}
                      onChange={({ detail }) => setFormData({
                        ...formData,
                        external_ids: { ...formData.external_ids, [key]: detail.value }
                      })}
                      placeholder="ID value"
                    />
                    <Button
                      variant="icon"
                      iconName="remove"
                      onClick={() => {
                        const newIds = { ...formData.external_ids };
                        delete newIds[key];
                        setFormData({ ...formData, external_ids: newIds });
                      }}
                    />
                  </SpaceBetween>
                ))}
                <SpaceBetween direction="horizontal" size="xs">
                  <Select
                    selectedOption={selectedProvider}
                    onChange={({ detail }) => {
                      setSelectedProvider(detail.selectedOption);
                      if (detail.selectedOption.value !== '__custom__') {
                        setNewExternalKey(detail.selectedOption.value);
                      } else {
                        setNewExternalKey('');
                      }
                    }}
                    options={KNOWN_MAM_PROVIDERS.filter(p => 
                      !Object.keys(formData.external_ids || {}).includes(p.value)
                    )}
                    placeholder="Select MAM provider"
                  />
                  {selectedProvider?.value === '__custom__' && (
                    <Input
                      value={newExternalCustomKey}
                      onChange={({ detail }) => {
                        setNewExternalCustomKey(detail.value);
                        setNewExternalKey(detail.value);
                      }}
                      placeholder="Custom system name"
                    />
                  )}
                  <Input
                    value={newExternalValue}
                    onChange={({ detail }) => setNewExternalValue(detail.value)}
                    placeholder="Asset ID"
                  />
                  <Button
                    onClick={() => {
                      if (newExternalKey && newExternalValue) {
                        setFormData({
                          ...formData,
                          external_ids: { ...formData.external_ids, [newExternalKey]: newExternalValue }
                        });
                        setNewExternalKey('');
                        setNewExternalValue('');
                        setNewExternalCustomKey('');
                        setSelectedProvider(null);
                      }
                    }}
                    disabled={!newExternalKey || !newExternalValue}
                  >
                    Add
                  </Button>
                </SpaceBetween>
              </SpaceBetween>
            </FormField>

            <FormField label="Audio Languages">
              <SpaceBetween size="s">
                <TokenGroup
                  items={formData.audio_languages.map(lang => ({ label: lang, value: lang }))}
                  onDismiss={({ detail }) => removeLanguage(formData.audio_languages[detail.itemIndex], 'audio_languages')}
                />
                <Input
                  placeholder="Enter language code (e.g., en, fr, es) and press Enter"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addLanguage(e.target.value, 'audio_languages');
                      e.target.value = '';
                    }
                  }}
                />
              </SpaceBetween>
            </FormField>

            <FormField label="Subtitle Languages">
              <SpaceBetween size="s">
                <TokenGroup
                  items={formData.subtitle_languages.map(lang => ({ label: lang, value: lang }))}
                  onDismiss={({ detail }) => removeLanguage(formData.subtitle_languages[detail.itemIndex], 'subtitle_languages')}
                />
                <Input
                  placeholder="Enter language code (e.g., en, fr, es) and press Enter"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addLanguage(e.target.value, 'subtitle_languages');
                      e.target.value = '';
                    }
                  }}
                />
              </SpaceBetween>
            </FormField>
          </SpaceBetween>
        </Form>
      </Modal>
    </>
  );
};

export default VersionsTab;